import { useEffect, useState } from 'react';

import { putLanguageData } from 'src/services/apiRequests/LanguageService';
import { colors } from 'src/styles/colors';
import {
  getLocalStorage,
  getSessionStorage,
  setCookie,
  setLocalStorage,
  setSessionStorage
} from 'src/utils/browserStorageHandler';
import {
  CHOOSE_MORE_LANGUAGES,
  YES_WOULD
} from 'src/utils/constants/ConsumptionPagesConstants';
import { CONTENT_LANGUAGES_COOKIE } from 'src/utils/constants/cookieConstants';
import {
  CONSUMPTION_LANGUAGE_FROM_PWA,
  CONSUMPTION_LANGUAGE_FROM_WEBAPP,
  PREVIOUS_CONSUMPTION_PAGE_LANGUAGES,
  SETTINGS,
  USER_TOKEN
} from 'src/utils/constants/localStorageConstants';
import {
  DisplayLanguage,
  LanguagesPopupEnums
} from 'src/utils/constants/stringConstants';

import { useAppStore } from 'src/hooks/useAppStore';
import SimpleStore from '../../store/SimpleStore';
import { HTTP_URL_PARAMS } from '../../utils/constants/HttpMethodsConstants';
import { LANGUAGE_LABELS } from '../../utils/languageConstants';
import Button from '../buttons/Button';
import Modal from '../modal/Modal';
import * as style from './styles';
import { Container } from './styles';
import {
  triggerClickMoreLanguagesMixpanelEvents,
  triggerClosePopupMixpanelEvents,
  triggerSaveSettingsMixpaneEvents
} from './utils';

type PropsType = {
  audioLanguages: string;
};

/**
 *  If the set of previous consumption language and user preference content language does not have a common element, show the popup.
 */

const LanguageChangePopup = ({ audioLanguages }: PropsType) => {
  const {
    usersPreferenceData,
    setUsersPreferenceData,
    setPopups,
    translations
  } = useAppStore();

  const consumptionPageStrings = translations!.consumption?.LANG_CHANGE_POPUP;
  const [consumptionPageAudioLanguage, setConsumptionPageAudioLanguage] =
    useState('');
  const [show, setShow] = useState(true);

  const displayLanguage =
    usersPreferenceData?.display_language ||
    getLocalStorage(SETTINGS)?.display_language ||
    DisplayLanguage.ENGLISH;
  const contentLanguageArray: string[] =
    usersPreferenceData?.content_language?.split(',') ||
    getLocalStorage(SETTINGS)?.content_language?.split(',') ||
    [];

  const consumptionPageLanguageArray = consumptionPageAudioLanguage
    ? consumptionPageAudioLanguage.split(',')
    : [];

  const commonContentLanguages = contentLanguageArray.filter(
    (value) => consumptionPageLanguageArray.indexOf(value) !== -1
  );

  let descriptionString = '';
  let likeWatchMoreString = '';
  let titleString = '';

  type keyOfLanguageLabels = keyof typeof LANGUAGE_LABELS;

  if (!commonContentLanguages.length) {
    const currentContentLanguage =
      LANGUAGE_LABELS?.[displayLanguage as keyOfLanguageLabels]?.[
        consumptionPageAudioLanguage?.substring(0, 2) as keyOfLanguageLabels
      ] || '';
    likeWatchMoreString = consumptionPageStrings?.LIKE_WATCH_MORE || '';
    titleString = likeWatchMoreString.replace(/<\S+> [\S*|\s*]+\?/, '').trim();
    descriptionString = likeWatchMoreString.replace(titleString, '');
    descriptionString = descriptionString.replace(
      /<\S+>/,
      currentContentLanguage
    );
  }

  const handleClose = () => {
    triggerClosePopupMixpanelEvents();
    setShow(false);
  };

  const handleChooseMoreLanguageClick = () => {
    triggerClickMoreLanguagesMixpanelEvents(CHOOSE_MORE_LANGUAGES);
    setPopups({ showLanguagePopup: true });
    setShow(false);
  };

  const handleChangeLanguageButton = () => {
    triggerSaveSettingsMixpaneEvents(YES_WOULD);
    const selectedAudioLanguage = consumptionPageAudioLanguage.split(',')[0];
    const savedContentLanguages =
      usersPreferenceData?.content_language?.split(',') || [];
    const updatedContentLanguages = savedContentLanguages?.find(
      (lang: string) => lang === selectedAudioLanguage
    )
      ? savedContentLanguages
      : [...savedContentLanguages, selectedAudioLanguage].sort();
    const updatedContentLanguagesString = updatedContentLanguages.join(',');
    if (usersPreferenceData) {
      const newUserPreferenceData = {
        ...usersPreferenceData,
        content_language: updatedContentLanguagesString
      };
      setUsersPreferenceData(newUserPreferenceData);
      setLocalStorage(SETTINGS, JSON.stringify(newUserPreferenceData));
    }
    setCookie(CONTENT_LANGUAGES_COOKIE, updatedContentLanguagesString);
    SimpleStore.set(
      HTTP_URL_PARAMS.CONTENT_LANGUAGE,
      updatedContentLanguagesString
    );

    const token = getLocalStorage(USER_TOKEN);
    if (token) {
      putLanguageData(
        LanguagesPopupEnums.CONTENT_LANGAUGE,
        updatedContentLanguagesString
      );
    }
    setShow(false);
  };

  useEffect(() => {
    const prevConsumptionPageLanguage =
      getSessionStorage(CONSUMPTION_LANGUAGE_FROM_PWA) ||
      getSessionStorage(PREVIOUS_CONSUMPTION_PAGE_LANGUAGES) ||
      '';
    setConsumptionPageAudioLanguage(prevConsumptionPageLanguage);
    setShow(true);
    setSessionStorage(PREVIOUS_CONSUMPTION_PAGE_LANGUAGES, audioLanguages);
    setSessionStorage(CONSUMPTION_LANGUAGE_FROM_PWA, '');
    setSessionStorage(CONSUMPTION_LANGUAGE_FROM_WEBAPP, audioLanguages);
  }, [audioLanguages]);

  return (
    <Modal
      show={
        show && !commonContentLanguages.length && !!consumptionPageAudioLanguage
      }
      onClose={handleClose}
      width="560px"
      height="350px"
      margin="0px 10px 0px 10px"
      background={colors.AppBgColor}
      renderCloseIcon={true}
      closeIconColor={colors.white}
      separateCloseIcon={true}
      padding="0px"
      handleResponseForSm={true}
      handleResponseForMobileXs={true}
      data-testid="languagesModal">
      <Container data-testid="languagesContainer">
        <div css={style.title} data-testid="languagesTitle">
          {titleString}
        </div>
        <h2 css={style.content} data-testid="languagesDescription">
          {descriptionString}
        </h2>
        <Button
          customStyles={style.button}
          callback={handleChangeLanguageButton}
          data-testid="languagesDescriptnBtn">
          {consumptionPageStrings?.YES_WOULD}
        </Button>
        <div
          css={style.chooseMoreLanguages}
          onClick={handleChooseMoreLanguageClick}
          data-testid="languagesDescriptnChooseLang">
          {consumptionPageStrings?.MORE_LANGUAGES}
        </div>
      </Container>
    </Modal>
  );
};

export default LanguageChangePopup;
